import { Link } from "@tanstack/react-router";
import { motion, useReducedMotion } from "motion/react";
import type { WorkItem } from "@/lib/work";
import { BrowserFrame, PhoneFrame, tintStyle } from "@/components/work/device-frames";

type Props = {
  item: WorkItem;
  /** Full-width tile: wider stage, the desktop frame sits left with the phone beside it. */
  lead?: boolean;
  eager?: boolean;
  index?: number;
};

/**
 * One project as a small scene: real desktop screenshot in a browser window,
 * real phone screenshot beside it, both peeking out of a brand-tinted card.
 * The card is the link; hovering lifts the devices a touch.
 */
export function WorkTile({ item, lead = false, eager = false, index = 0 }: Props) {
  const reduce = useReducedMotion();
  const ease = "ease-[cubic-bezier(0.22,1,0.36,1)]";
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.7, delay: Math.min(index, 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className="h-full"
    >
      <Link
        to="/work/$slug"
        params={{ slug: item.slug }}
        aria-label={`${item.name}: ${item.title}`}
        className="group relative flex h-full flex-col overflow-hidden rounded-[2rem] bg-secondary/60 ring-1 ring-foreground/[0.06] transition-shadow duration-500 hover:shadow-[0_30px_80px_-40px_rgba(10,10,20,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric"
        style={tintStyle(item.accent)}
      >
        <div className="flex items-start justify-between gap-5 p-7 md:p-8">
          <div className="min-w-0">
            <h3 className="font-display text-xl tracking-[-0.025em] md:text-2xl">{item.name}</h3>
            <p className={`mt-1.5 text-pretty leading-snug text-muted-foreground ${lead ? "max-w-[40ch] text-base md:text-lg" : "max-w-[32ch] text-[15px]"}`}>
              {item.title}
            </p>
          </div>
          <span className="shrink-0 rounded-full bg-background/70 px-3 py-1 text-[11px] font-medium tracking-wide text-foreground/70 ring-1 ring-foreground/[0.06] backdrop-blur">
            {item.category}
          </span>
        </div>

        <div className={`relative mt-auto aspect-[16/9] ${lead ? "md:aspect-[2.2/1]" : ""}`}>
          <BrowserFrame
            shot={item.cover}
            host={item.host}
            dark={item.dark}
            eager={eager}
            className={`absolute left-7 top-1 w-full transition-transform duration-700 ${ease} group-hover:-translate-y-2 md:left-8 ${
              lead ? "md:w-[76%]" : ""
            }`}
          />
          {item.mobile ? (
            <PhoneFrame
              shot={item.mobile}
              radius={lead ? "1.9rem" : "1.5rem"}
              className={`absolute bottom-[-14%] right-5 w-[23%] max-w-[150px] transition-transform delay-75 duration-700 ${ease} group-hover:-translate-y-3 md:right-7 ${
                lead ? "md:bottom-[-8%] md:right-10 md:w-[16%] md:max-w-[210px]" : ""
              }`}
            />
          ) : null}
        </div>
      </Link>
    </motion.div>
  );
}
